'use client';

/**
 * UI-INFO: Obvestilo o zmogljivostih in omejitvah Tribonianusa.
 *
 * Skladnost s Smernicami MDP za UI v javni upravi (sekcija 2.4 Preglednost) in
 * Uredbo (EU) 2024/1689 (AI Act) — pravica uporabnika do informacij o tem,
 * kaj sistem zmore, kakšne so omejitve in od kod črpa podatke.
 *
 * Dostopno prek info ikone v Headerju.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Info, Sparkles, AlertTriangle, Database, Shield, FileText, Linkedin, Globe, Mail } from 'lucide-react';
import { LinkedInQR } from '@/components/about/LinkedInQR';

interface InfoDialogProps {
  trigger?: React.ReactNode;
}

export function InfoDialog({ trigger }: InfoDialogProps) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="O sistemu Tribonianus"
          >
            <Info className="h-4 w-4" />
            <span className="sr-only">O sistemu</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-primary" />
            O sistemu Tribonianus
          </DialogTitle>
          <DialogDescription>
            AI pravni pomočnik za pravnike. Podpira analizo predpisov, sodne prakse in nomotehnični pregled.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(85vh-160px)] pr-4">
          <div className="space-y-5 py-2 text-sm">
            {/* Kaj sistem zmore */}
            <section>
              <h3 className="font-semibold flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Kaj sistem zmore
              </h3>
              <ul className="space-y-1.5 list-disc list-inside text-muted-foreground ml-1">
                <li>Iskanje po slovenski zakonodaji (PISRS — register predpisov in 211.000+ členov)</li>
                <li>Iskanje po odločbah Ustavnega sodišča RS (16.000+ odločb) in komentarjih Ustave (e-KURS)</li>
                <li>Iskanje po sodni praksi nacionalnih sodišč in pravu EU (EUR-Lex, CJEU)</li>
                <li>Nomotehnični pregled osnutkov predpisov v 8 fazah (oblika, jezik, citiranje, ustavnost, EU)</li>
                <li>Generiranje pravnih mnenj v slogu SVZ in dnevni dosje za posamezne zadeve</li>
                <li>Inline citiranje virov v odgovorih (klikne se za odprtje izvirnika)</li>
              </ul>
            </section>

            {/* Omejitve */}
            <section>
              <h3 className="font-semibold flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                Omejitve, ki jih je treba poznati
              </h3>
              <ul className="space-y-1.5 list-disc list-inside text-muted-foreground ml-1">
                <li>
                  <strong>AI lahko halucinira</strong> — odgovor je lahko slovnično in stilsko brezhiben,
                  vsebinsko pa napačen. Vedno preverite citate in vire.
                </li>
                <li>
                  <strong>Sistem ni avtoritativen vir.</strong> Njegovi odgovori in priporočila zahtevajo
                  pregled in odobritev pravnika SVZ pred uradno uporabo.
                </li>
                <li>
                  <strong>Jezikovni model</strong> (Google Gemini 2.5) ima časovno omejitev znanja —
                  najnovejši dogodki morda niso zajeti, čeprav so podatki PISRS in sodne prakse
                  posodobljeni dnevno.
                </li>
                <li>
                  <strong>Občinski predpisi</strong> in nestrukturirani stari akti pred letom 1995 niso v
                  iskalnem indeksu (glejte register PISRS neposredno).
                </li>
                <li>
                  <strong>Sistem si ne zapomni</strong> vsebine med različnimi sejami. Vse zadeve in
                  dokumenti so shranjeni le v vašem brskalniku.
                </li>
              </ul>
            </section>

            {/* Viri podatkov */}
            <section>
              <h3 className="font-semibold flex items-center gap-2 mb-2">
                <Database className="h-4 w-4 text-blue-600" />
                Viri podatkov
              </h3>
              <ul className="space-y-1.5 list-disc list-inside text-muted-foreground ml-1">
                <li>
                  <strong>PISRS</strong> — Pravno-informacijski sistem RS (pisrs.si): register predpisov,
                  besedila in NPB verzije
                </li>
                <li>
                  <strong>USRS</strong> — Ustavno sodišče RS (us-rs.si): odločbe in sklepi Ustavnega sodišča
                </li>
                <li>
                  <strong>Sodna praksa</strong> — Vrhovno sodišče RS (sodnapraksa.si): odločbe nacionalnih sodišč
                </li>
                <li>
                  <strong>EUR-Lex</strong> — Urad za publikacije EU (eur-lex.europa.eu): zakonodaja EU,
                  CJEU sodna praksa, CELLAR knowledge graph
                </li>
                <li>
                  <strong>e-KURS</strong> — Komentar Ustave RS (Nova univerza): doktrinarni viri
                </li>
                <li>
                  <strong>Nomotehnične smernice</strong> — Služba Vlade RS za zakonodajo: 478 robnih številk
                </li>
              </ul>
            </section>

            {/* Pravna ureditev */}
            <section>
              <h3 className="font-semibold flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-emerald-600" />
                Pravna ureditev
              </h3>
              <ul className="space-y-1.5 list-disc list-inside text-muted-foreground ml-1">
                <li>
                  <strong>Uredba (EU) 2024/1689</strong> (AI Act) — Tribonianus je klasificiran kot sistem
                  z <strong>omejenim tveganjem</strong> (čl. 50): uporabnike obvešča, da gre za AI, in
                  označuje AI-generirano vsebino.
                </li>
                <li>
                  <strong>ZIUDHPUI</strong> (UL RS 85/25) — Zakon o izvajanju uredbe (EU) o določitvi
                  harmoniziranih pravil o umetni inteligenci.
                  Skladnost sistema z zahtevami zakona se zagotavlja sproti.
                </li>
                <li>
                  <strong>ZVOP-2 in GDPR</strong> — Uporabniki naj v Tribonianus <strong>ne nalagajo
                  osebnih podatkov</strong>. Naložene dokumente Google Gemini procesira na strežnikih EU,
                  vendar Tribonianus še nima vzpostavljene avtomatske anonimizacije (PII redakcija je v razvoju).
                </li>
                <li>
                  Za vprašanja o varstvu podatkov se obrnite na avtorja (kontakt spodaj).
                </li>
              </ul>
            </section>

            {/* Kako interpretirati odgovore */}
            <section>
              <h3 className="font-semibold flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-purple-600" />
                Kako interpretirati odgovore
              </h3>
              <ul className="space-y-1.5 list-disc list-inside text-muted-foreground ml-1">
                <li>
                  <strong>Iskalne kartice</strong> nad odgovorom kažejo dejanske vire iz registrov.
                  Te so verodostojne — kliknite, da odprete izvirnik.
                </li>
                <li>
                  <strong>Inline citati</strong> (
                  <span className="font-mono text-[11px] bg-muted px-1 py-0.5 rounded">[REF:1]</span>
                  ) v besedilu povezujejo trditve z viri. Klik odpre dokument in označi mesto.
                </li>
                <li>
                  <strong>Verifikacija citatov</strong> (zelene/oranžne/rdeče oznake) označuje, kako
                  zanesljivo Tribonianus podpre trditev z dejanskim besedilom predpisa.
                </li>
                <li>
                  <strong>Word izvozi</strong> imajo v nogi opozorilo o AI-izvoru za vse, ki dokument
                  prejmejo.
                </li>
              </ul>
            </section>

            {/* Avtor & kontakt */}
            <div className="pt-4 border-t">
              <p className="text-xs text-muted-foreground mb-3">
                Tribonianus je <strong>osebni projekt</strong>, ki ga razvijam na lastno iniciativo, v
                aktivnem razvoju. Povratne informacije, napake in predlogi so dobrodošli.
              </p>
              <div className="flex items-center gap-4 rounded-lg border bg-muted/30 p-3">
                {/* QR — LinkedIn */}
                <a
                  href="https://www.linkedin.com/in/andrejsersen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 rounded-md border bg-white p-1.5 transition-transform hover:scale-[1.03]"
                  title="Odpri LinkedIn profil"
                >
                  <LinkedInQR className="h-20 w-20" />
                </a>
                {/* Kontakt */}
                <div className="min-w-0">
                  <p className="text-sm font-medium" style={{ fontFamily: 'var(--font-cormorant), serif', letterSpacing: '0.02em' }}>
                    Andrej Seršen
                  </p>
                  <p className="text-[11px] text-muted-foreground mb-2">Zasnova in razvoj, 2026</p>
                  <div className="flex flex-wrap gap-1.5">
                    <a href="https://www.linkedin.com/in/andrejsersen" target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors hover:bg-muted">
                      <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                    </a>
                    <a href="https://sersen.eu" target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors hover:bg-muted">
                      <Globe className="h-3.5 w-3.5" /> sersen.eu
                    </a>
                    <a href="mailto:andrej@sersen.eu"
                       className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors hover:bg-muted">
                      <Mail className="h-3.5 w-3.5" /> andrej@sersen.eu
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
